import type { DensitySnapshot, WrappedCoin } from "@screener/protocol";
import { Sheet, SheetContent, SheetTitle } from "@screener/uikit/components/sheet";
import { memo, useEffect, useState } from "react";
import { tickerLabel, venueOf, wrappedMark } from "@/lib/labels";
import type { ActionNotice } from "./action-notice";
import { DensityDetailContent, type DetailState } from "./density-detail-content";
import type { FavoriteToggleNotice } from "./favorite-notice";
import { panelFinalFocus } from "./focus-handoff";
import type { HideCoinNotice } from "./hidden-coin-notice";
import type { NoteWriteResult } from "./notes-api";
import type { RowPinMatch } from "./pin-match";
import type { NotesState } from "./use-notes";

interface DensityDetailPanelProps {
  /** Перечень обёрнутых монет из приветствия. */
  wrapped: readonly WrappedCoin[];
  /** Открытая плотность из потока; `null` — панель закрыта. */
  row: DensitySnapshot | null;
  state: DetailState;
  onRefresh: () => void;
  onClose: () => void;
  notesState: NotesState;
  onCreateNote: (base: string, text: string) => Promise<NoteWriteResult>;
  onUpdateNoteText: (id: string, text: string) => Promise<NoteWriteResult>;
  onDeleteNote: (id: string) => Promise<"ok" | "not-found" | "unavailable">;
  /**
   * Избранное §6.6 монеты открытой плотности — то же сопоставление и то же
   * действие, что у строки таблицы (web-density-detail «Звёздочка панели и
   * звёздочка строки показывают одно состояние»).
   */
  favoriteBases: Set<string> | null;
  onToggleFavorite: (row: DensitySnapshot) => void;
  pendingFavoriteBases: ReadonlySet<string>;
  /**
   * Отказ последней записи избранного — назван в панели, не только в общей
   * полосе. Несёт монету (review stage6-favorites, п. 5): панель показывает
   * его, только если он о её собственной монете.
   */
  favoriteNotice: FavoriteToggleNotice;
  /** Скрыть монету открытой плотности целиком (§75 п. 2). */
  onHideCoin: (base: string) => void;
  /** Отказ последнего скрытия — назван в панели, если он о её монете. */
  hideNotice: HideCoinNotice;
  /** Исход копирования или отправки в терминал — то же состояние экрана, что у статус-бара. */
  actionNotice: ActionNotice | null;
  /** Панель открыта действием «заметки» со строки §9. */
  focusNotes: boolean;
  /** Скопировать тикер этой плотности (§9 1.44) — действие §9, недоступное в карточке §9 (1.21). */
  onCopyTicker: (row: DensitySnapshot) => void;
  /** Закрепить уровень этой плотности или снять уже стоящее закрепление. */
  onTogglePin: (row: DensitySnapshot) => void;
  /** Закрепление §6.7, сопоставленное с открытой плотностью — то же, что видит строка. */
  pinMatch: RowPinMatch | "unknown" | undefined;
  /** Свой запрос закрепления открытой плотности ещё не завершился. */
  pinPending: boolean;
}

/**
 * Оболочка детальной панели §9. Всё, что она делает, — открывает, закрывает
 * и возвращает фокус; содержимое живёт своим компонентом, потому что
 * `Sheet` уходит в портал (см. `density-detail-content.tsx`).
 *
 * Под `memo` (design.md stage9-render-scope, решение 4): пропсы экран держит
 * стабильными, и панель перерисовывается, только когда изменилось то, что
 * она показывает.
 */
export const DensityDetailPanel = memo(function DensityDetailPanel({
  wrapped,
  row,
  state,
  onRefresh,
  onClose,
  notesState,
  onCreateNote,
  onUpdateNoteText,
  onDeleteNote,
  favoriteBases,
  onToggleFavorite,
  pendingFavoriteBases,
  favoriteNotice,
  onHideCoin,
  hideNotice,
  actionNotice,
  focusNotes,
  onCopyTicker,
  onTogglePin,
  pinMatch,
  pinPending,
}: DensityDetailPanelProps) {
  // `row` уходит в `null` в том же кадре, где `Sheet` уходит в `open={false}`,
  // а выезд занимает ~200мс: без этого удержания панель доигрывала бы анимацию
  // пустой, а заголовок на середине пути подменялся бы общим «Детали плотности».
  const [lastShown, setLastShown] = useState<{ row: DensitySnapshot; state: DetailState } | null>(
    row === null ? null : { row, state },
  );
  useEffect(() => {
    if (row !== null) {
      setLastShown({ row, state });
    }
  }, [row, state]);

  const displayedRow = row ?? lastShown?.row ?? null;
  const displayedState = row !== null ? state : (lastShown?.state ?? state);

  return (
    <Sheet
      open={row !== null}
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
    >
      <SheetContent
        data-detail-panel
        // После «Скрыть монету» вернуть фокус некуда — строка-триггер удалена;
        // он идёт на соседнюю строку (Д8), в остальных случаях — как обычно.
        finalFocus={panelFinalFocus}
        className="w-[min(640px,92vw)] sm:max-w-none data-[side=right]:sm:max-w-none"
      >
        {/* Заголовок нужен читающему с экрана: своё название панели стоит
            в содержимом версткой, а не заголовком диалога. */}
        <SheetTitle className="sr-only">
          {displayedRow === null
            ? "Детали плотности"
            : [
                `Детали плотности ${venueOf(displayedRow.instrument)}`,
                tickerLabel(displayedRow.instrument, displayedRow.exchange_base),
                wrappedMark(displayedRow.instrument.base, wrapped),
              ]
                .filter((part) => part !== undefined)
                .join(" ")}
        </SheetTitle>
        {displayedRow === null ? null : (
          <DensityDetailContent
            wrapped={wrapped}
            row={displayedRow}
            state={displayedState}
            onRefresh={onRefresh}
            notesState={notesState}
            onCreateNote={onCreateNote}
            onUpdateNoteText={onUpdateNoteText}
            onDeleteNote={onDeleteNote}
            favoriteBases={favoriteBases}
            onToggleFavorite={onToggleFavorite}
            pendingFavoriteBases={pendingFavoriteBases}
            favoriteNotice={favoriteNotice}
            onHideCoin={onHideCoin}
            hideNotice={hideNotice}
            actionNotice={actionNotice}
            focusNotes={focusNotes}
            onCopyTicker={onCopyTicker}
            onTogglePin={onTogglePin}
            pinMatch={pinMatch}
            pinPending={pinPending}
          />
        )}
      </SheetContent>
    </Sheet>
  );
});
